import { useGetPaginatedNotificationsQuery, useMarkAllNotificationsAsReadMutation } from '@notifications/services';
import { LoadableNotification } from '@notifications/types';
import { handleError } from '@utils/common';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

export const useNotifications = () => {
    const [unreadNotificationsTotalCount, setUnreadNotificationsTotalCount] = useState<number>(0);
    const [readNotifications, setReadNotifications] = useState<LoadableNotification[]>([]);
    const [unreadNotifications, setUnreadNotifications] = useState<LoadableNotification[]>([]);
    const [isReadActionRunning, setIsReadActionRunning] = useState(false);
    const processedStreamIdsRef = useRef(new Set<LoadableNotification['id']>());

    const intl = useIntl();

    const { data: hasNotificationsData } = useGetPaginatedNotificationsQuery({
        is_read: false,
        page: 1,
        page_size: 1,
    });
    const hasNotifications = hasNotificationsData && hasNotificationsData.count > 0;

    const [markAllRead, { isLoading: isMarkAllReadLoading }] = useMarkAllNotificationsAsReadMutation();

    const handleMarkAllAsRead = async () => {
        if (isReadActionRunning) {
            return;
        }

        setIsReadActionRunning(true);

        try {
            await markAllRead().unwrap();
            setUnreadNotifications((prevNotifications) => {
                const updatedNotifications = prevNotifications.map((notification) => ({
                    ...notification,
                    is_read: true,
                }));
                setReadNotifications((prevReadNotifications) => [...updatedNotifications, ...prevReadNotifications]);
                return [];
            });
            setUnreadNotificationsTotalCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            handleError(
                intl.formatMessage({
                    id: 'notifications.markAllAsReadError',
                    defaultMessage: 'Failed to mark all notifications as read.',
                }),
                error
            );
        } finally {
            setIsReadActionRunning(false);
        }
    };

    return {
        unreadNotificationsTotalCount,
        setUnreadNotificationsTotalCount,
        readNotifications,
        setReadNotifications,
        unreadNotifications,
        setUnreadNotifications,
        isReadActionRunning,
        setIsReadActionRunning,
        hasNotifications,
        handleMarkAllAsRead,
        isMarkAllReadLoading,
        processedStreamIdsRef,
    };
};
